import { CheckCircle } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatBottomNav from '../components/chat/ChatBottomNav';
import PasswordTextField from '../components/common/PasswordTextField';
import AppHeader from '../components/layout/AppHeader';
import AccountSection from '../components/profile/AccountSection';
import DangerZoneSection from '../components/profile/DangerZoneSection';
import ProviderSection from '../components/profile/ProviderSection';
import API_ENDPOINTS from '../config/api';
import { useAuth } from '../contexts/AuthContext';

interface Avatar {
  id: number;
  display_name: string;
  image_url: string;
  order: number;
}

interface UserProfile {
  id: string;
  email: string;
  name: string;
  display_name?: string;
  date_joined?: string;
  subscription_provider_name?: string;
  active_membership?: {
    id: string;
    provider: {
      id: string;
      business_name: string;
    };
    site: {
      id: string;
      name: string;
    };
    joined_at: string;
  } | null;
  provider_membership_policy?: {
    same_provider_cooldown_seconds: number;
    min_membership_seconds: number;
  };
  phone?: string;
  tags: string[];
  voice_gender?: string;
  voice_language?: string;
  selected_avatar?: number;
  selected_avatar_details?: {
    id: number;
    display_name: string;
    image_url: string;
  };
}

const FONT_FAMILY =
  'Poppins, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif';

const SECTION_SX = {
  bgcolor: '#FFFFFF',
  borderRadius: '20px',
  filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.15))',
  px: 2.5,
  py: 2.5,
  mb: 2,
};

const SECTION_HEADING_SX = {
  fontFamily: FONT_FAMILY,
  fontWeight: 700,
  fontSize: 18,
  color: '#000000',
  mb: 2,
};

const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    fontFamily: FONT_FAMILY,
  },
  '& .MuiInputLabel-root': {
    fontFamily: FONT_FAMILY,
  },
  mb: 2,
};

const ProfilePage: React.FC = () => {
  const { deleteAccount, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Profile form fields
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [voiceGender, setVoiceGender] = useState('male');
  const [voiceLanguage, setVoiceLanguage] = useState('en-GB');
  const [selectedAvatar, setSelectedAvatar] = useState<number | null>(null);
  const [avatarOptions, setAvatarOptions] = useState<Avatar[]>([]);
  // Password form fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [leavingProvider, setLeavingProvider] = useState(false);

  useEffect(() => {
    loadProfile();
    loadAvatars();
  }, []);

  const loadProfile = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.AUTH.USER_INFO);
      const userData = response.data.user;
      setProfile(userData);
      setName(userData.name || '');
      setDisplayName(userData.display_name || '');
      setPhone(userData.phone || '');
      setVoiceGender(userData.voice_gender || 'male');
      setVoiceLanguage(userData.voice_language || 'en-GB');
      setSelectedAvatar(userData.selected_avatar || null);
    } catch (err) {
      console.error('Failed to load profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadAvatars = async (): Promise<void> => {
    try {
      const response = await axios.get(API_ENDPOINTS.AUTH.AVATARS);

      // Handle both paginated and non-paginated responses
      const avatars = response.data.results || response.data;
      setAvatarOptions(Array.isArray(avatars) ? avatars : []);
    } catch (err) {
      console.error('Failed to load avatars:', err);
    }
  };

  const handleSaveProfile = async (): Promise<void> => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const updateData: Record<string, string | number | null> = {
        name: name.trim(),
        display_name: displayName.trim(),
        voice_gender: voiceGender,
        voice_language: voiceLanguage,
        selected_avatar: selectedAvatar,
      };

      if (phone.trim()) {
        updateData.phone = phone.trim();
      }

      const response = await axios.patch(
        API_ENDPOINTS.AUTH.PROFILE,
        updateData,
      );
      setProfile(response.data);
      setSuccess('Profile updated successfully');
    } catch (err: any) {
      console.error('Failed to update profile:', err);
      setError(
        err.response?.data?.detail || 'Failed to update profile',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (): Promise<void> => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      if (!currentPassword || !newPassword) {
        setError('Please fill in all password fields');
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('New passwords do not match');
        return;
      }
      if (newPassword.length < 8) {
        setError('New password must be at least 8 characters long');
        return;
      }

      await axios.post(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, {
        old_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: confirmPassword,
      });

      setSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      console.error('Failed to change password:', err);
      const data = err.response?.data;
      // Reason: DRF serializer returns field-level errors, not just "detail"
      let message = 'Failed to change password';
      if (data?.detail) {
        message = data.detail;
      } else if (data?.old_password) {
        message = Array.isArray(data.old_password)
          ? data.old_password[0]
          : data.old_password;
      } else if (data?.new_password) {
        message = Array.isArray(data.new_password)
          ? data.new_password[0]
          : data.new_password;
      }
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = (): void => {
    logout();
    navigate('/login');
  };

  const handleDeleteAccount = async (): Promise<void> => {
    try {
      setDeletingAccount(true);
      await deleteAccount();
      navigate('/login');
    } catch {
      setError('Failed to delete account. Please try again.');
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleLeaveProvider = async (): Promise<boolean> => {
    try {
      setLeavingProvider(true);
      setError(null);
      setSuccess(null);
      await axios.post(API_ENDPOINTS.AUTH.PROVIDER_MEMBERSHIP_LEAVE);
      await loadProfile();
      await refreshUser();
      setSuccess('You have left this provider.');
      return true;
    } catch (err: any) {
      setError(
        err.response?.data?.detail ||
        'Failed to leave provider. Please try again.',
      );
      return false;
    } finally {
      setLeavingProvider(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: 'var(--app-height, 100vh)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const memberSinceText = (() => {
    if (profile?.date_joined) {
      const d = new Date(profile.date_joined);
      const month = d.toLocaleString('default', { month: 'long' });
      return 'Member since %s %d'
        .replace('%s', month)
        .replace('%d', String(d.getFullYear()));
    }
    return 'Member since —';
  })();

  const displayUsername =
    displayName.trim() || name.trim() || 'Username';

  return (
    <Box
      sx={{
        height: 'var(--app-height, 100vh)',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#FFFFFF',
      }}
    >
      <AppHeader title="Profile" />

      <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 3, pt: 2, WebkitOverflowScrolling: 'touch' }}>
        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }}>
            {success}
          </Alert>
        )}

        {/* Yellow panel — user identity header */}
        <Box
          sx={{
            bgcolor: 'rgba(255, 212, 0, 0.65)',
            borderRadius: '25px',
            filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
            px: 2.5,
            py: 3,
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontWeight: 700,
              fontSize: 32,
              color: '#000000',
            }}
          >
            {displayUsername}
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontWeight: 400,
              fontSize: 15,
              color: '#828282',
              mt: 0.5,
            }}
          >
            {profile?.email} · {memberSinceText}
          </Typography>
        </Box>

        {/* Profile details section */}
        <Box sx={SECTION_SX}>
          <Typography sx={SECTION_HEADING_SX}>Profile</Typography>

          <TextField
            fullWidth
            label="Full Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            sx={INPUT_SX}
          />
          <TextField
            fullWidth
            label="Display Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={saving}
            helperText="Name shown in conversations"
            sx={INPUT_SX}
          />
          <TextField
            fullWidth
            label="Email"
            value={profile?.email || ''}
            disabled
            helperText="Email cannot be changed"
            sx={INPUT_SX}
          />
          <TextField
            fullWidth
            label="Phone Number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={saving}
            placeholder="For support services you allow to contact you"
            helperText="For support services you allow to contact you"
            sx={INPUT_SX}
          />

          {/* Avatar selection */}
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontWeight: 600,
              fontSize: 15,
              color: '#000000',
              mb: 1.5,
            }}
          >
            Choose your avatar
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
            {Array.isArray(avatarOptions) && avatarOptions.length > 0 ? (
              avatarOptions.map((avatar) => (
                <Box
                  key={avatar.id}
                  onClick={() => setSelectedAvatar(avatar.id)}
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '36px',
                    border: selectedAvatar === avatar.id
                      ? '3px solid #FFD400'
                      : '2px solid #D8DADC',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    position: 'relative',
                    flexShrink: 0,
                    transition: 'border-color 0.2s',
                    '&:hover': { borderColor: '#FFD400' },
                  }}
                >
                  <Box
                    component="img"
                    src={avatar.image_url}
                    alt={avatar.display_name}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                  {selectedAvatar === avatar.id && (
                    <CheckCircle
                      sx={{
                        position: 'absolute',
                        bottom: 2,
                        right: 2,
                        fontSize: 20,
                        color: '#FFD400',
                        bgcolor: '#FFFFFF',
                        borderRadius: '50%',
                      }}
                    />
                  )}
                </Box>
              ))
            ) : (
              <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: 14, color: '#999' }}>
                No avatars available. Please upload avatars via Django Admin.
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl fullWidth disabled={saving} sx={INPUT_SX}>
              <InputLabel>Identity</InputLabel>
              <Select
                value={voiceGender}
                label="Identity"
                onChange={(e) => setVoiceGender(e.target.value)}
              >
                <MenuItem value="male">Jeff</MenuItem>
                <MenuItem value="female">Jess</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={saving} sx={INPUT_SX}>
              <InputLabel>Voice Language</InputLabel>
              <Select
                value={voiceLanguage}
                label="Voice Language"
                onChange={(e) => setVoiceLanguage(e.target.value)}
              >
                <MenuItem value="en-GB">English (UK)</MenuItem>
                <MenuItem value="en-US">English (US)</MenuItem>
                <MenuItem value="es-ES">Spanish</MenuItem>
                <MenuItem value="fr-FR">French</MenuItem>
                <MenuItem value="de-DE">German</MenuItem>
                <MenuItem value="it-IT">Italian</MenuItem>
                <MenuItem value="pt-PT">Portuguese</MenuItem>
                <MenuItem value="nl-NL">Dutch</MenuItem>
                <MenuItem value="sv-SE">Swedish</MenuItem>
                <MenuItem value="nb-NO">Norwegian</MenuItem>
                <MenuItem value="da-DK">Danish</MenuItem>
                <MenuItem value="fi-FI">Finnish</MenuItem>
                <MenuItem value="pl-PL">Polish</MenuItem>
                <MenuItem value="cs-CZ">Czech</MenuItem>
                <MenuItem value="hu-HU">Hungarian</MenuItem>
                <MenuItem value="ro-RO">Romanian</MenuItem>
                <MenuItem value="tr-TR">Turkish</MenuItem>
                <MenuItem value="el-GR">Greek</MenuItem>
                <MenuItem value="ga-IE">Irish</MenuItem>
                <MenuItem value="cy-GB">Welsh</MenuItem>
                <MenuItem value="ja-JP">Japanese</MenuItem>
                <MenuItem value="zh-CN">Chinese (Mandarin)</MenuItem>
                <MenuItem value="ko-KR">Korean</MenuItem>
                <MenuItem value="ar-SA">Arabic</MenuItem>
                <MenuItem value="hi-IN">Hindi</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Button
            variant="contained"
            onClick={handleSaveProfile}
            disabled={saving}
            sx={{
              bgcolor: '#FFD400',
              color: '#000000',
              fontFamily: FONT_FAMILY,
              fontWeight: 700,
              borderRadius: '12px',
              px: 4,
              py: 1.25,
              textTransform: 'none',
              '&:hover': { bgcolor: '#E6BF00' },
              filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
            }}
          >
            {saving ? <CircularProgress size={20} /> : 'Save Profile'}
          </Button>
        </Box>

        {/* Change password section */}
        <Box sx={SECTION_SX}>
          <Typography sx={SECTION_HEADING_SX}>
            Change Password
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: 13,
              color: '#666',
              fontStyle: 'italic',
              mb: 2,
            }}
          >
            Password must be at least 8 characters long
          </Typography>

          <PasswordTextField
            fullWidth
            label="Current Password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            disabled={saving}
            sx={INPUT_SX}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <PasswordTextField
              fullWidth
              label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={saving}
              sx={INPUT_SX}
            />
            <PasswordTextField
              fullWidth
              label="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={saving}
              sx={INPUT_SX}
            />
          </Box>

          <Button
            variant="contained"
            onClick={handleChangePassword}
            disabled={saving || !currentPassword || !newPassword}
            sx={{
              bgcolor: '#FFD400',
              color: '#000000',
              fontFamily: FONT_FAMILY,
              fontWeight: 700,
              borderRadius: '12px',
              px: 4,
              py: 1.25,
              textTransform: 'none',
              '&:hover': { bgcolor: '#E6BF00' },
              filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
            }}
          >
            {saving ? (
              <CircularProgress size={20} />
            ) : (
              'Change Password'
            )}
          </Button>
        </Box>

        {/* Account section */}
        <AccountSection
          userId={profile?.id || ''}
          subscriptionProviderName={profile?.subscription_provider_name || ''}
          activeMembership={profile?.active_membership}
          sameProviderCooldownSeconds={
            profile?.provider_membership_policy?.same_provider_cooldown_seconds
          }
          leavingProvider={leavingProvider}
          onLogout={handleLogout}
          onLeaveProvider={handleLeaveProvider}
        />

        {!profile?.active_membership && (
          <ProviderSection
            onJoinManually={(code) => navigate(`/join/${code}`)}
          />
        )}

        {/* Danger Zone section */}
        <DangerZoneSection
          userEmail={profile?.email || ''}
          deletingAccount={deletingAccount}
          onDeleteAccount={handleDeleteAccount}
        />
      </Box>

      <ChatBottomNav />
    </Box>
  );
};

export default ProfilePage;
